import * as Actions from '../actions/matterStageActions';
import MatterStage from '../../entities/matterStage';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type MatterStageAction = { 
    type: string; 
    matterStage: MatterStage | null; 
    matterStageId: string | MatterStage; 
    partial: boolean; 
    lastFetch: number; 
    matterStages: MatterStage[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<MatterStage> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {MatterStage[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<MatterStage> | null, firmId: string, userId: string, updates?: MatterStage[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'matterStage', 'matterStages', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<MatterStage>} data
 * @param {MatterStage[]} [updates]
 * @returns {ArrayMap<MatterStage>}
 */
function updateStorage(data: ArrayMap<MatterStage>, updates?: MatterStage[]): ArrayMap<MatterStage> {
	if (logging) console.debug('⮂ matterStageReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {MatterStage[]} [updates]
 * @returns {ArrayMap<MatterStage>}
 */
function updateStorageNoReturn(updates?: MatterStage[]): void {
	if (logging) console.debug('⮂ matterStageReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {MatterStage[]} [data]
 * @returns {ArrayMap<MatterStage>}
 */
function createState(data?: MatterStage[] | ArrayMap<MatterStage>): ArrayMap<MatterStage> {
	if (!data) return ArrayMapRegistry.clone<MatterStage>('matterStage', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<MatterStage>('matterStage', data.map(e => e));
    else return ArrayMapRegistry.clone<MatterStage>('matterStage', data);
}

function matterStages(state: ArrayMap<MatterStage>, action: MatterStageAction): ArrayMap<MatterStage> {
	if (!state) state = createState();

	let st: ArrayMap<MatterStage>;

	switch (action.type) {
		// action.matterStage - the new value of matterStage with id action.id
		// action.matterStageId - id of the target, used if new value is null
		case Actions.MATTERSTAGE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ matterStageReducer: FETCH_ONE_SUCCESS', action);

			if (action.matterStage === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.matterStageId)) {
				st = state.clone();
				st.set(action.matterStage);
				// state.clear();

				updateStorageNoReturn([action.matterStage]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.matterStages
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.matterStages
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.matterStages
			 !partial, !stateEmpty, fetchEmpty	-> D return matterStages in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge matterStages in state newer than those in action.matterStages
		 */
		case Actions.MATTERSTAGE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.matterStages.length === 0;
			const fetched = action.matterStages.map(matterStage => new MatterStage({...matterStage, persisted: true}));

			if (logging) console.debug(`⮂ matterStageReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} matterStages with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ matterStageReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ matterStageReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<MatterStage> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: MatterStage[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(matterStage => {
					const current = st.get(matterStage.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && matterStage.status === 'D') {
							// Remove it from state
							toUpdate.push(matterStage);
							st.delete(matterStage);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (matterStage.addedOn > current.addedOn) {
								toUpdate.push(matterStage);
								st.set(matterStage);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (matterStage.status !== 'D') {
							toUpdate.push(matterStage);
							st.set(matterStage);
						}
						else if (logging) console.debug('⮂ matterStageReducer: partialMerge: skipping deleted matterStage not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ matterStageReducer: empty: matterStages', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ matterStageReducer: fullMerge: matterStages', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localMatterStage => {
                    // If this matterStage has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localMatterStage.persisted) {
                        const fetchedMatterStage = st.get(localMatterStage.id);
                        if (!fetchedMatterStage || localMatterStage.addedOn > fetchedMatterStage.addedOn) st.set(localMatterStage);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.MATTERSTAGE_FETCH_ONE_FAILURE:
			console.error('MATTERSTAGE_FETCH_ONE_FAILURE', action.matterStageId, action.err);

			// No update to storage required
			return state;

		case Actions.MATTERSTAGE_FETCH_FAILURE:
			console.error('MATTERSTAGE_FETCH_FAILURE', action.matterStageId, action.err);

			// No update to storage required
			return state;

		case Actions.MATTERSTAGE_RESTORE: {
		    let matterStage = new MatterStage({...action.matterStage});
            st = state.clone();

            const existing = st.get(matterStage.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (matterStage.addedOn >= existing.addedOn) {
                    st.set(matterStage);
                    return updateStorage(st, [matterStage]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (matterStage.status === 'D') return state;

                st.set(matterStage);
                return updateStorage(st, [matterStage]);
            }
        }
            
		case Actions.MATTERSTAGE_SAVE: {
		    // console.debug('⮂ MATTERSTAGE_SAVE reducer', action.matterStage?.id);
		    
			// clone the matterStage
			let matterStage = new MatterStage({...action.matterStage});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new matterStage in the state
			// If this is a create, add to the queue
			const _matterStage = st.get(matterStage.id);

			if (!_matterStage) st.set(matterStage);
			else {
				if (matterStage.addedOn >= _matterStage.addedOn) st.set(matterStage);
			}

			return updateStorage(st, [matterStage]);
		}

		case Actions.MATTERSTAGE_SAVE_MANY: {
		    if (logging) console.debug('⮂ MATTERSTAGE_SAVE_MANY reducer', action.matterStages.map(matterStage => matterStage.id).join(','));
		    
			// clone the matterStages
			let toProcess = action.matterStages.map(matterStage => new MatterStage(matterStage));

			st = state.clone();
			// state.clear();

			const updates: MatterStage[] = [];

			toProcess.forEach(matterStage => {
				// Add or update the new matterStage in the state
				// If this is a create, add to the queue
				const existing = st.get(matterStage.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (matterStage.status !== 'D') {
						st.push(matterStage);
						updates.push(matterStage);
					}
					else if (logging) console.debug('⮂ MATTERSTAGE_SAVE_MANY: skipping deleted matterStage not in state');
				}
				else {
					if (matterStage.addedOn >= existing.addedOn) {
						st.set(matterStage);
						updates.push(matterStage);
					}
					else if (logging) console.debug('⮂ Skipping, existing matterStage had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.MATTERSTAGE_REMOVE: {
			// clone the matterStage
			const matterStage = new MatterStage({...action.matterStage});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(matterStage);

			return updateStorage(st, [matterStage]);
		}

		case Actions.MATTERSTAGE_RESTORE_FAILURE:
		case Actions.MATTERSTAGE_SAVE_FAILURE: {
		    console.warn('MATTERSTAGE_SAVE_FAILURE reducer', action.matterStage?.id);
		    if (!action.matterStage?.id) {
				console.warn('MATTERSTAGE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const matterStage = ensure(MatterStage, action.matterStage);
			if (!matterStage) {
				console.error('Could not find matterStage after call to save');
				return state;
			}

			st = state.clone();

			const _matterStageRef = state.get(matterStage.id);

			if (_matterStageRef && (_matterStageRef.addedOn === matterStage.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _matterStage = new MatterStage({..._matterStageRef});
				_matterStage.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_matterStage.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('matterStage', _matterStage).then(r => {
						if (r.isErr()) console.error(`⮂ matterStageReducer: failed to save matterStage with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ matterStageReducer: saved matterStage with fatalError info');
					}).catch(err => console.error(`⮂ matterStageReducer: failed to save matterStage with fatalError info: ${err.message}`));

				st.set(_matterStage);
			}

			return updateStorage(st);
		}

		case Actions.MATTERSTAGE_SAVE_MANY_FAILURE: {
		    console.warn('MATTERSTAGE_SAVE_MANY_FAILURE reducer', action.matterStages.map(matterStage => matterStage.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.MATTERSTAGE_RESTORE_SUCCESS:
		case Actions.MATTERSTAGE_SAVE_SUCCESS: {
			if (!action.matterStage?.id) {
				console.warn('MATTERSTAGE_SAVE_SUCCESS reducer');
                return state;
			}

			const matterStageRef = state.get(action.matterStage?.id);
			if (!matterStageRef) {
				console.warn('Could not find matterStage after call to save');
                return state;
			}

			st = state.clone();

			if (action.matterStage.addedOn >= matterStageRef.addedOn) {
				st.set(action.matterStage);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const matterStage = new MatterStage({...matterStageRef, persisted: true});
				st.set(matterStage);
			}

			return updateStorage(st, [action.matterStage]);
		}

		case Actions.MATTERSTAGE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ MATTERSTAGE_SAVE_MANY_SUCCESS reducer', action.matterStages.map(matterStage => matterStage.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const matterStages: MatterStage[] = [];

			action.matterStages.forEach(e => {
				const matterStageRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!matterStageRef) console.warn('Could not find matterStage after call to save');
				else if (e.addedOn >= matterStageRef.addedOn) {
				    st.set(e);
				    matterStages.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const matterStage = new MatterStage({...matterStageRef, persisted: true});
					st.set(matterStage);
					matterStages.push(matterStage);
				}
			});

			return updateStorage(st, matterStages);
		}

		case Actions.MATTERSTAGE_REMOVE_FAILURE: {
		    console.warn('MATTERSTAGE_REMOVE_FAILURE action', action.matterStage?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.MATTERSTAGE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.MATTERSTAGE_RESTORE_MANY: {
			if (logging) console.debug('⮂ MATTERSTAGE_RESTORE_MANY reducer', action.matterStages.map(matterStage => matterStage.id).join(','));

			const toProcess = action.matterStages.map(matterStage => new MatterStage({...matterStage}));
			st = state.clone();

			const updates: MatterStage[] = [];

			toProcess.forEach(matterStage => {
				const existing = st.get(matterStage.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (matterStage.addedOn >= existing.addedOn) {
						st.set(matterStage);
						updates.push(matterStage);
					}
					else if (logging) console.debug('⮂ MATTERSTAGE_RESTORE_MANY: skipping older version of matterStage');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (matterStage.status !== 'D') {
						st.set(matterStage);
						updates.push(matterStage);
					}
					else if (logging) console.debug('⮂ MATTERSTAGE_RESTORE_MANY: skipping deleted matterStage not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.MATTERSTAGE_REMOVE_MANY: {
			if (logging) console.debug('⮂ MATTERSTAGE_REMOVE_MANY reducer', action.matterStages.map(matterStage => matterStage.id).join(','));

			const toProcess = action.matterStages.map(matterStage => new MatterStage({...matterStage}));
			st = state.clone();

			toProcess.forEach(matterStage => {
				st.delete(matterStage);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default matterStages;